/**
 * useVersionPoller — Capa 1 del sistema de auto-actualización agresivo.
 *
 * Cómo funciona:
 * 1. Al hacer build, Vite genera dist/version.json con un timestamp único.
 * 2. Este hook consulta /version.json cada POLL_INTERVAL ms.
 * 3. Si la versión del servidor es diferente a la que cargó el browser → recarga automática.
 * 4. En visibilitychange (volver de otra tab / desbloquear teléfono) verifica inmediatamente.
 *
 * Resultado: todas las TVs y celulares se actualizan en máximo 2 minutos tras un deploy.
 */

import { useEffect, useRef } from 'react';

const POLL_INTERVAL = 2 * 60 * 1000; // 2 minutos

export function useVersionPoller() {
    const currentVersion = useRef<string | null>(null);

    const checkVersion = async () => {
        try {
            // Cache-bust the request so the browser never serves a stale version.json
            const res = await fetch(`/version.json?t=${Date.now()}`, {
                cache: 'no-store',
            });
            if (!res.ok) return;

            const data = await res.json();
            const serverVersion = data?.version;

            if (!serverVersion) return;

            if (currentVersion.current === null) {
                // Primera vez: guardamos la versión actual del cliente
                currentVersion.current = serverVersion;
            } else if (currentVersion.current !== serverVersion) {
                // Nueva versión detectada → recarga limpia sin confirmación
                console.info(`[VersionPoller] Nueva versión detectada (${serverVersion}). Recargando...`);
                window.location.reload();
            }
        } catch {
            // Sin conexión — ignorar, volver a intentar en el siguiente ciclo
        }
    };

    useEffect(() => {
        // Verificar inmediatamente al montar
        checkVersion();

        // Polling periódico
        const intervalId = setInterval(checkVersion, POLL_INTERVAL);

        // Verificar al volver a la app (desbloquear teléfono / cambiar de tab)
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                checkVersion();
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);

        // Verificar también al recuperar conexión
        const handleOnline = () => checkVersion();
        window.addEventListener('online', handleOnline);

        return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('online', handleOnline);
        };
    }, []);
}
